// Frame 11's IMPACT RADIUS card (33:1915) — PRD 16 §5 B2, "change this term
// and 14 documents must change with it".
//
// SOURCE CHANGED, and the reason is the whole panel. This was built from the
// vocabulary-health findings' `doc_ids`, which cannot express B2's own headline
// sentence: health findings are three ranked, TOP_N-capped work-lists;
// `stalls` and `unexplained` each carry exactly one document, so they
// contribute rows whose radius is 1 by construction; and a concept defined
// CONSISTENTLY across fourteen documents is never an `inconsistent` finding at
// all — which is precisely the concept a change-control conversation is about.
//
// It now reads `GET /dashboards/:org/impact-radius`, built from the corpus
// glossary's `member_refs` — the Pass 2 provenance that records which document
// each merged candidate came from. `backend/analytics/impact_radius.py`'s
// docstring carries the rest.
//
// THE UN-RECONCILED STATE IS ITS OWN STATE, not an empty list. `member_refs`
// exist only once Pass 2 has run, and `auto_reconcile` swallows its own
// failures by design, so a fully published corpus can have none. "No concept
// spans two documents" and "reconciliation has not run" are opposite claims and
// only the first is a reason to publish another document.
//
// `tone` is stated, never derived from the value: a wide radius is a fact about
// coupling, not a failure, so these bars stay nominal.

import { Card, EmptyState, MeterBar } from "../../ui";
import type { ImpactRadiusReport } from "./api";
import { shownOf } from "./format";
import {
  RADIUS_EMPTY_BODY,
  RADIUS_EMPTY_HEADLINE,
  RADIUS_SUBTITLE,
  RADIUS_TITLE,
  RADIUS_UNRECONCILED_BODY,
  RADIUS_UNRECONCILED_HEADLINE,
} from "./metricLabels";
import "./analytics.css";

/** A work-list, not an inventory — same reasoning as `corpus/health.py`'s
 *  TOP_N. The total is always stated beside it. */
const MAX_ROWS = 5;
const MAX_DOC_CHIPS = 4;

export function ImpactRadius({ report }: { report: ImpactRadiusReport }) {
  const shown = report.rows.slice(0, MAX_ROWS);
  const max = shown[0]?.doc_count ?? 0;

  return (
    <Card
      header={RADIUS_TITLE}
      footer={
        <span className="type-bodyS">
          {report.reconciled && report.concepts_found > 0
            ? `${shownOf(shown.length, report.concepts_found, "concept")} · ${report.concepts_spanning} shared by more than one document`
            : RADIUS_SUBTITLE}
        </span>
      }
    >
      {!report.reconciled ? (
        <EmptyState
          headline={RADIUS_UNRECONCILED_HEADLINE}
          body={RADIUS_UNRECONCILED_BODY}
        />
      ) : report.concepts_spanning === 0 ? (
        <EmptyState headline={RADIUS_EMPTY_HEADLINE} body={RADIUS_EMPTY_BODY} />
      ) : (
        <ul className="maisight-analytics-meters" aria-label="Impact radius by concept">
          {shown.map((row) => (
            <li key={row.entity_id} className="maisight-analytics-meter-row">
              <div className="maisight-analytics-meter-head">
                <span className="maisight-analytics-term type-numSm">{row.term}</span>
                <span className="type-numSm maisight-analytics-muted">
                  {row.doc_count === 1 ? "1 document" : `${row.doc_count} documents`}
                </span>
              </div>
              <MeterBar
                value={row.doc_count}
                max={max}
                tone="nominal"
                label={`${row.term} appears in ${row.doc_count} documents`}
              />
              {/* The documents themselves, because "14 documents" is the
               *  headline and "which fourteen" is the next question. */}
              <p className="maisight-analytics-meter-meta type-bodyS">
                {row.doc_ids.slice(0, MAX_DOC_CHIPS).join(", ")}
                {row.doc_ids.length > MAX_DOC_CHIPS
                  ? `, +${row.doc_ids.length - MAX_DOC_CHIPS} more`
                  : ""}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
